import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { scrollToId } from "@/lib/scroll";
import { CLUB } from "@/data/content";

const LINKS = [
  { label: "Home", target: "#home" },
  { label: "Chi Siamo", target: "#chi-siamo" },
  { label: "Direttivo", target: "#direttivo" },
  { label: "Vieni allo Stadio", target: "#stadio" },
  { label: "Tesseramento", target: "#tesseramento" },
  { label: "News", target: "#news" },
  { label: "Galleria", target: "#galleria" },
  { label: "Contatti", target: "#contatti" },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    setTimeout(() => scrollToId(target), 80);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass bg-background/80 border-b border-border shadow-lg" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <button data-testid="nav-logo-link" onClick={() => go("#home")} className="flex items-center gap-3">
          <img src={CLUB.logo} alt="Logo Inter Club Guardialfiera" className="h-10 md:h-14 w-auto drop-shadow-lg" />
          <span className="hidden sm:block font-display font-extrabold uppercase tracking-tight leading-none text-sm md:text-base">
            Inter Club<br />
            <span className="text-primary">Guardialfiera</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-link-${l.target.replace("#", "")}`}
              onClick={() => go(l.target)}
              className="px-3 py-2 text-sm font-semibold uppercase tracking-wide text-foreground/80 hover:text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle-button"
            onClick={toggle}
            aria-label="Cambia tema"
            className="p-2 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            data-testid="nav-membership-cta-button"
            onClick={() => go("#tesseramento")}
            className="hidden sm:inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
          >
            Diventa Socio
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 rounded-full border border-border"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden glass bg-background/95 border-b border-border"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.target}
                  data-testid={`nav-mobile-link-${l.target.replace("#", "")}`}
                  onClick={() => go(l.target)}
                  className="text-left px-3 py-3 rounded-lg font-semibold uppercase tracking-wide hover:bg-secondary transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-membership-cta"
                onClick={() => go("#tesseramento")}
                className="mt-2 rounded-full bg-primary px-5 py-3 font-bold uppercase tracking-wide text-primary-foreground"
              >
                Diventa Socio
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
